import { APP_NAME } from '../config/branding';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

export const initGA = () => {
  // Ensure GTM's dataLayer exists
  window.dataLayer = window.dataLayer || [];
};

type DataLayerPayload = Record<string, any> & { event?: string };

const gtmPush = (payload: DataLayerPayload) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

export const logPageView = (path: string, title?: string) => {
    // Get hp from session if available
    let campaignId: number | undefined;
    try {
      const session = sessionStorage.getItem('helppetai_session');
      if (session) {
        const sessionData = JSON.parse(session);
        campaignId = sessionData.hp;
      }
    } catch (error) {
      // Ignore parsing errors
    }
    
  gtmPush({
    event: 'page_view',
      page_path: path,
      page_title: title || document.title,
    page_location: window.location.href,
    ...(campaignId && { campaign_id: campaignId }),
    });
};

export const logEvent = (category: string, action: string, label?: string) => {
  gtmPush({
    event: 'custom_event',
      event_category: category,
    event_action: action,
      event_label: label,
    });
};

// Custom events for your veterinary practice site
export const trackPracticeView = (practiceName: string, slug: string) => {
  logEvent('Practice', 'View Practice Page', `${practiceName} (${slug})`);
};

export const trackPhoneClick = (practiceName: string, phone: string) => {
  logEvent('Contact', 'Click Phone Number', `${practiceName}: ${phone}`);
};

export const trackEmailClick = (practiceName: string, email: string) => {
  logEvent('Contact', 'Click Email', `${practiceName}: ${email}`);
};

export const trackWebsiteClick = (practiceName: string, website: string) => {
  logEvent('Contact', 'Click Website', `${practiceName}: ${website}`);
};

export const trackSocialClick = (practiceName: string, platform: string, url: string) => {
  logEvent('Social', `Click ${platform}`, practiceName);
};

export const trackDemoRequest = () => {
  logEvent('Conversion', 'Request Demo', 'Header CTA');
};

export const trackSignup = (type: 'vet' | 'pet_owner') => {
  gtmPush({
    event: 'sign_up',
    method: type,
  });
};

// ===== ENGAGEMENT TRACKING =====

// AI Front Desk Audio Player
export const trackAudioPlay = () => {
  gtmPush({
    event: 'audio_play',
      event_category: 'engagement',
      event_label: 'AI Front Desk Sample Call',
    value: 5,
    });
};

export const trackAudioComplete = (duration: number) => {
  gtmPush({
    event: 'audio_complete',
      event_category: 'engagement',
      event_label: 'AI Front Desk Sample Call',
      duration: Math.round(duration),
    value: 10,
    });
};

export const trackAudioProgress = (percentage: number) => {
  gtmPush({
    event: 'audio_progress',
      event_category: 'engagement',
      event_label: `${percentage}% complete`,
    value: 1,
    });
};

// Video Tracking
export const trackVideoPlay = () => {
  gtmPush({
    event: 'video_play',
      event_category: 'engagement',
      event_label: `${APP_NAME} Demo Video`,
    value: 10,
    });
};

export const trackVideoComplete = () => {
  gtmPush({
    event: 'video_complete',
      event_category: 'engagement',
      event_label: `${APP_NAME} Demo Video`,
    value: 20,
    });
};

// Savings Calculator
export const trackCalculatorInteraction = (callsPerDay: number) => {
  gtmPush({
    event: 'calculator_interaction',
      event_category: 'engagement',
      event_label: 'Phone Calls Savings Calculator',
      calls_per_day: callsPerDay,
    value: 3,
    });
};

// Feature Cards (Dashboard)
export const trackFeatureCardClick = (featureName: string) => {
  gtmPush({
    event: 'feature_card_click',
      event_category: 'engagement',
      event_label: featureName,
    value: 2,
    });
};

// Transcript View
export const trackTranscriptView = () => {
  gtmPush({
    event: 'transcript_view',
      event_category: 'engagement',
      event_label: 'AI Call Transcript',
    value: 3,
    });
};

// ===== CONVERSION TRACKING =====

// App Download
export const trackAppDownload = (source: string = 'header') => {
  gtmPush({
    event: 'app_download',
      event_category: 'conversion',
    source,
    value: 50,
    });
};

// Get Started CTA
export const trackGetStarted = (source: string = 'hero') => {
  gtmPush({
    event: 'get_started_click',
      event_category: 'conversion',
    source,
    value: 15,
    });
};

// Demo Request
export const trackDemoRequestEnhanced = (source: string = 'header', email?: string) => {
  gtmPush({
    event: 'request_demo',
      event_category: 'conversion',
    source,
      has_email: !!email,
    value: 25,
    });
};

// Phone Trial Click
export const trackPhoneTrialClick = () => {
  gtmPush({
    event: 'phone_trial_click',
      event_category: 'conversion',
      event_label: 'Try it: 425-584-1920',
    value: 10,
    });
};

// Christmas Card CTA
export const trackChristmasCardClick = () => {
  gtmPush({
    event: 'christmas_card_click',
    event_category: 'conversion',
    event_label: 'World\'s Best PIMS - Christmas 2025',
    value: 20,
  });
};

// Online Tour
export const trackOnlineTour = () => {
  gtmPush({
    event: 'online_tour_click',
      event_category: 'engagement',
      event_label: 'Take an Online Tour',
    value: 5,
    });
};

// FAQ Interaction
export const trackFaqClick = (question: string) => {
  gtmPush({
    event: 'faq_click',
      event_category: 'engagement',
      event_label: question,
    value: 1,
    });
};

// Pricing Page View
export const trackPricingView = () => {
  gtmPush({
    event: 'pricing_page_view',
      event_category: 'engagement',
      event_label: 'View Pricing Page',
    value: 8,
    });
};

// Product Page Views
export const trackProductPageView = (product: 'ai_front_desk' | 'website_hosting' | 'visit_transcriptions' | 'online_booking' | 'calendar' | 'proto_pims' | 'inventory_management' | 'two_way_texting' | 'mobile_forms') => {
    const productLabels = {
      ai_front_desk: 'AI Front Desk',
      website_hosting: 'Website Hosting',
      visit_transcriptions: 'Visit Transcriptions',
      online_booking: 'Online Booking',
      calendar: 'Calendar & Scheduling',
      proto_pims: 'Medical Records Beta',
      inventory_management: 'Inventory Management',
      two_way_texting: '2-Way Texting',
      mobile_forms: 'Mobile Forms',
    };
    
  gtmPush({
    event: `product_page_view_${product}`,
      event_category: 'product_pages',
      event_label: productLabels[product],
    value: 10,
    });
};

// ===== USER IDENTIFICATION =====

// Set user properties when user logs in
export const identifyUser = (userId: string, userRole: string, practiceId?: string) => {
    const hpTag = getHpTag();
    
  // Persist user context in the dataLayer so GTM tags can read it.
  gtmPush({
      user_id: userId,
      user_role: userRole,
      ...(practiceId && { practice_id: practiceId }),
    ...(hpTag && { hp_tag: hpTag }),
    });
    
  // Emit an event for GTM triggers / debugging
  gtmPush({
    event: 'user_identified',
    user_id: userId,
    user_role: userRole,
    ...(practiceId && { practice_id: practiceId }),
    ...(hpTag && { hp_tag: hpTag }),
  });
};

// Clear user properties on logout
export const clearUserIdentification = () => {
  gtmPush({
    user_id: null,
    user_role: null,
    practice_id: null,
  });

  gtmPush({
    event: 'user_logout',
  });
};

// ===== HP TAG (CRM TRACKING) =====

// Get HP tag from URL or session
export const getHpTag = (): string | null => {
  try {
    // First, check if already in session
    const session = sessionStorage.getItem('helppetai_session');
    if (session) {
      const sessionData = JSON.parse(session);
      if (sessionData.hp) {
        return sessionData.hp;
      }
    }
    
    // Check URL for hp parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hpFromUrl = urlParams.get('hp');
    
    if (hpFromUrl) {
      // Store in session for persistence
      const sessionData = { hp: hpFromUrl };
      sessionStorage.setItem('helppetai_session', JSON.stringify(sessionData));
      return hpFromUrl;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting HP tag:', error);
    return null;
  }
};

// Initialize HP tag tracking (call on app load)
export const initializeHpTracking = () => {
  const hpTag = getHpTag();
  
  if (hpTag) {
    // Persist for GTM variables
    gtmPush({ hp_tag: hpTag });
    
    // Track capture as an event
    gtmPush({
      event: 'hp_tag_captured',
      event_category: 'crm_tracking',
      event_label: `HP: ${hpTag}`,
      hp_tag: hpTag,
      value: 1,
    });
  }
};

